import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function BlogManager() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(data);
    } catch {
      toast.error("Could not load posts.");
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "",
      meta_description: p.meta_description || "",
      image_url: p.image_url || "",
      tags: (p.tags || []).join(", "),
      content_html: p.content_html || "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/blog/${editing.slug}`,
        {
          ...form,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        },
        { withCredentials: true }
      );
      toast.success("Post updated.");
      setEditing(null);
      load();
    } catch {
      toast.error("Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/blog/${confirmDelete.slug}`, { withCredentials: true });
      toast.success("Post deleted.");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  if (posts === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-secondary-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="blog-manager">
      {posts.length === 0 ? (
        <p className="text-muted-foreground font-light py-16 text-center border border-border" data-testid="blog-empty">
          No posts yet. Outrank will publish here automatically.
        </p>
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="blog-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs tracking-[0.15em] uppercase">Title</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase">Published</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase">Tags</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((p) => (
                <TableRow key={p.slug} data-testid={`blog-row-${p.slug}`}>
                  <TableCell className="font-medium max-w-sm">
                    <span className="block truncate">{p.title}</span>
                    <span className="block text-xs text-muted-foreground font-light">/blog/{p.slug}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm font-light">{formatDate(p.published_at)}</TableCell>
                  <TableCell className="text-sm font-light">{(p.tags || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button
                      data-testid={`blog-edit-${p.slug}`}
                      onClick={() => openEdit(p)}
                      className="p-2 text-secondary-foreground hover:text-foreground transition-colors"
                      aria-label={`Edit ${p.title}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`blog-delete-${p.slug}`}
                      onClick={() => setConfirmDelete(p)}
                      className="p-2 text-secondary-foreground hover:text-destructive transition-colors"
                      aria-label={`Delete ${p.title}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-none" data-testid="blog-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light tracking-tight">Edit post</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-6 pt-2">
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-2">Title</label>
                <Input
                  data-testid="blog-edit-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-2">
                  Meta description
                </label>
                <Input
                  data-testid="blog-edit-meta"
                  value={form.meta_description}
                  onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-2">
                  Image URL
                </label>
                <Input
                  data-testid="blog-edit-image"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-2">
                  Tags (comma separated)
                </label>
                <Input
                  data-testid="blog-edit-tags"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-2">
                  Content (HTML)
                </label>
                <Textarea
                  data-testid="blog-edit-content"
                  rows={10}
                  value={form.content_html}
                  onChange={(e) => setForm({ ...form, content_html: e.target.value })}
                  className="rounded-none font-mono text-xs"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEditing(null)}
                  className="rounded-none text-xs tracking-[0.2em] uppercase"
                >
                  Cancel
                </Button>
                <Button
                  onClick={save}
                  disabled={saving || !form.title.trim()}
                  data-testid="blog-edit-save"
                  className="rounded-none bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent className="max-w-md rounded-none" data-testid="blog-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light tracking-tight">Delete this post?</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-light text-muted-foreground leading-relaxed">
            "{confirmDelete?.title}" will be permanently removed from the blog. This cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              className="rounded-none text-xs tracking-[0.2em] uppercase"
            >
              Cancel
            </Button>
            <Button
              onClick={remove}
              disabled={deleting}
              data-testid="blog-delete-confirm"
              className="rounded-none bg-destructive text-destructive-foreground text-xs tracking-[0.2em] uppercase"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
